import { type FC, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  usePollsListQuery,
  type LibraryPollsItem,
} from "@/partner/entities/polls";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { interpolationFormat } from "@/partner/shared/lib";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { PollsLibraryListToolbar } from "./PollsLibraryListToolbar";

interface PollsLibraryListProps {
  onClickRow: (poll: LibraryPollsItem) => void;
}

/** Список библиотеки в стиле виджетов: Card + MUI Table, выделение строк. */
export const PollsLibraryList: FC<PollsLibraryListProps> = ({ onClickRow }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search },
  } = useFilter();
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const { data, loading } = usePollsListQuery({
    variables: { search: search || undefined, page, rowsPerPage },
    fetchPolicy: "cache-and-network",
  });

  const nodes = data?.polls.nodes ?? [];
  const count = data?.polls.count ?? 0;
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < nodes.length;

  const onSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelected(
      event.target.checked
        ? nodes.reduce((all, item) => ({ ...all, [item.id]: true }), {})
        : {}
    );
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (nodes.length === 0) {
    return <EmptyListCard title={t("pollsLibrary.list.empty")} />;
  }

  return (
    <Card elevation={0}>
      {numSelected > 0 && (
        <PollsLibraryListToolbar
          indeterminate={indeterminate}
          numSelected={numSelected}
          nodes={nodes}
          onSelectAllClick={onSelectAllClick}
          selectedIds={selectedIds}
          onCloseSelected={() => setSelected({})}
        />
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="default"
                indeterminate={indeterminate}
                checked={numSelected === nodes.length}
                onChange={onSelectAllClick}
              />
            </TableCell>
            <TableCell>{t("pollsLibrary.list.question")}</TableCell>
            {!isMobile && (
              <TableCell align="right">
                {t("pollsLibrary.list.options")}
              </TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">
                {t("pollsLibrary.list.attachments")}
              </TableCell>
            )}
            {!isMobile && (
              <TableCell align="right">
                {t("pollsLibrary.list.created")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.map((item) => (
            <TableRow
              key={item.id}
              hover
              selected={Boolean(selected[item.id])}
              onClick={() => onClickRow(item)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell
                padding="checkbox"
                onClick={(event) => event.stopPropagation()}
              >
                <Checkbox
                  color="default"
                  checked={Boolean(selected[item.id])}
                  onChange={(event) =>
                    setSelected({
                      ...selected,
                      [item.id]: event.target.checked,
                    })
                  }
                />
              </TableCell>
              <TableCell>
                <ItemTitleLink
                  to={`${PagePaths.polls}?tab=edit&id=${item.id}`}
                  onOpen={() => onClickRow(item)}
                  color="text.primary"
                  underline="none"
                >
                  {item.question}
                </ItemTitleLink>
              </TableCell>
              {!isMobile && (
                <TableCell align="right">{item.options.length}</TableCell>
              )}
              {!isMobile && (
                <TableCell align="right">
                  {item.usage.attachmentsCount}
                </TableCell>
              )}
              {!isMobile && (
                <TableCell align="right">
                  {interpolationFormat(item.createdAt, "short", i18n.language)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
